import React, { useState, useCallback, useMemo } from 'react';
import { Button, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import VipSuppIdTable from './components/VipSuppIdTable';
import VipSuppIdDrawer from './components/VipSuppIdDrawer';
import { getVipSuppIdList, getVipSuppIdStats } from './api';
import { VipSuppIdSearchParams, VipSuppIdRecord } from './types';
import { QUERY_KEYS } from './constants';

const DEFAULT_PAGE_SIZE = 10;

const VipSuppIdPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVipSuppId, setEditingVipSuppId] = useState<VipSuppIdRecord | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [QUERY_KEYS.VIP_SUPPID_LIST, currentPage, pageSize],
    queryFn: async () => {
      return getVipSuppIdList({
        currentPage,
        pageSize,
      });
    },
  });

  const { data: statsData } = useQuery({
    queryKey: [QUERY_KEYS.VIP_SUPPID_STATS],
    queryFn: getVipSuppIdStats,
  });

  const handleTableChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // 切换每页条数时重置到第一页
    }
  }, [pageSize]);

  const openDrawer = useCallback((vipSuppId: VipSuppIdRecord | null = null) => {
    setEditingVipSuppId(vipSuppId);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditingVipSuppId(null);
  }, []);

  const handleDrawerSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // 计算占用率
  const occupancyRate = useMemo(() => {
    if (!statsData || statsData.allCount === 0) {
      return 0;
    }
    return (statsData.usedCount / statsData.allCount) * 100;
  }, [statsData]);

  // 判断是否需要标红提示
  const shouldShowWarning = occupancyRate >= 80;

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Button type="primary" onClick={() => openDrawer(null)}>
          新增
        </Button>
        {statsData && (
          <span style={{ color: shouldShowWarning ? '#ff4d4f' : 'inherit' }}>
            VIP SUPPID 占用情况: {statsData.usedCount} / {statsData.allCount}
            {shouldShowWarning && '，请及时补充'}
          </span>
        )}
      </Space>

      <VipSuppIdTable
        data={data?.records || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.total || 0,
          onChange: handleTableChange,
        }}
        onEdit={openDrawer}
      />

      <VipSuppIdDrawer
        open={drawerOpen}
        editingVipSuppId={editingVipSuppId}
        onClose={closeDrawer}
        onSuccess={handleDrawerSuccess}
      />
    </div>
  );
};

export default VipSuppIdPage;

